"use client";

import type { LandingEventInput } from "@/lib/analytics/landing-events";

const SID_KEY = "jana_la_sid";
const ENDPOINT = "/api/landing/track";

let cachedSid: string | null = null;
let createdThisLoad = false;

function randomId(): string {
	try {
		if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
	} catch {
		/* fall through */
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Stable anonymous visitor id, persisted in localStorage so repeat visits by
 * the same browser collapse into one "unique visitor". Falls back to a
 * per-tab id when storage is unavailable (private mode, blocked cookies).
 */
export function getSessionId(): string {
	if (cachedSid) return cachedSid;
	try {
		const existing = localStorage.getItem(SID_KEY);
		if (existing) {
			cachedSid = existing;
			return existing;
		}
		const fresh = randomId();
		localStorage.setItem(SID_KEY, fresh);
		cachedSid = fresh;
		createdThisLoad = true;
		return fresh;
	} catch {
		cachedSid = randomId();
		createdThisLoad = true;
		return cachedSid;
	}
}

/**
 * True when this page load created a brand-new visitor id (drives the
 * one-shot `unique_session` event).
 */
export function isFirstVisit(): boolean {
	return createdThisLoad;
}

export interface LandingSectionMeta {
	sectionId?: string;
	sectionType?: string;
	sectionName?: string;
}

/**
 * Reads the identity of the section an element sits inside, from the
 * `data-*` attributes on its enclosing `[data-landing-section]` wrapper.
 */
export function readSectionMeta(el: Element | null | undefined): LandingSectionMeta {
	const host = el?.closest<HTMLElement>("[data-landing-section]");
	if (!host) return {};
	return {
		sectionId: host.dataset.sectionId || undefined,
		sectionType: host.dataset.sectionType || undefined,
		sectionName: host.dataset.sectionName || undefined,
	};
}

/**
 * Reads which page an element is rendered on, from the
 * `data-landing-page-id` attribute `PlatformLanding` sets on its root
 * `<main>` — lets deeply nested client components (e.g. the Contact form)
 * tag their own events without needing `pageId` threaded through every
 * section's props.
 */
export function readPageId(el: Element | null | undefined): string {
	return el?.closest<HTMLElement>("[data-landing-page-id]")?.dataset.landingPageId ?? "";
}

/**
 * Fire-and-forget event send. Uses sendBeacon so it survives page unload,
 * with a keepalive fetch fallback. Never throws.
 */
export function sendLandingEvent(input: Omit<LandingEventInput, "sessionId" | "referrer">): void {
	if (typeof window === "undefined") return;
	const payload: LandingEventInput = {
		...input,
		sessionId: getSessionId(),
		referrer: document.referrer || undefined,
	};
	const body = JSON.stringify(payload);
	try {
		if (navigator.sendBeacon) {
			const ok = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
			if (ok) return;
		}
	} catch {
		/* fall through to fetch */
	}
	try {
		void fetch(ENDPOINT, {
			method: "POST",
			body,
			headers: { "content-type": "application/json" },
			keepalive: true,
		}).catch(() => {});
	} catch {
		/* give up silently — analytics must never break the page */
	}
}
